import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/utils/queryClient';
import { trackAddToCart } from '@/lib/fbPixel';

// Critical pages loaded immediately
import AgeGate from "@/pages/AgeGate";
import HomePage from "@/pages/HomePage";

// Lazy load all other pages for faster initial load
const ProductsPageOptimized = lazy(() => import("@/pages/ProductsPageOptimized"));
const ProductDetailPageOptimized = lazy(() => import("@/pages/ProductDetailPageOptimized"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const LocationsPage = lazy(() => import("@/pages/LocationsPage"));
const RetailersPage = lazy(() => import("@/pages/RetailersPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const SalesPolicyPage = lazy(() => import("@/pages/SalesPolicyPage"));
const CompliancePolicyPage = lazy(() => import("@/pages/CompliancePolicyPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LoginChoice = lazy(() => import("@/pages/LoginChoice"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const CustomerSignupPage = lazy(() => import("@/pages/CustomerSignupPage"));
const EmployeeLoginPage = lazy(() => import("@/pages/EmployeeLoginPage"));
const CustomerDashboard = lazy(() => import("@/pages/CustomerDashboard"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const StaffSignupPage = lazy(() => import("@/pages/StaffSignupPage"));
const StaffLoginPage = lazy(() => import("@/pages/StaffLoginPage"));
const StaffDashboard = lazy(() => import("@/pages/StaffDashboard"));
const StaffProducts = lazy(() => import("@/pages/StaffProducts"));
const StaffWholesaleProducts = lazy(() => import("@/pages/StaffWholesaleProducts"));
const StaffOrders = lazy(() => import("@/pages/StaffOrders"));
const StaffContent = lazy(() => import("@/pages/StaffContent"));
const StaffHomeBanners = lazy(() => import("@/pages/StaffHomeBanners"));
const StaffIdVerifications = lazy(() => import("@/pages/StaffIdVerifications"));
const StaffSettings = lazy(() => import("@/pages/StaffSettings"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const StaffAdminSettings = lazy(() => import("@/pages/StaffAdminSettings"));
const StaffPromotions = lazy(() => import("@/pages/StaffPromotions"));
const StaffSMSBroadcast = lazy(() => import("@/pages/StaffSMSBroadcast"));
const WholesaleSignupPage = lazy(() => import("@/pages/WholesaleSignupPage"));
const WholesaleLoginPage = lazy(() => import("@/pages/WholesaleLoginPage"));
const WholesaleProductsPage = lazy(() => import("@/pages/WholesaleProductsPage"));
const WholesaleCheckoutPage = lazy(() => import("@/pages/WholesaleCheckoutPage"));
const StaffAllOrders = lazy(() => import("@/pages/StaffAllOrders"));
const COAsPage = lazy(() => import("@/pages/COAsPage"));
const StaffCOAs = lazy(() => import("@/pages/StaffCOAs"));
const StaffSystemManagement = lazy(() => import("@/pages/StaffSystemManagement"));
const StaffFiles = lazy(() => import("@/pages/StaffFiles"));
const VitaVerdePage = lazy(() => import("@/pages/VitaVerdePage"));
const ScavengerHuntPage = lazy(() => import("@/pages/ScavengerHuntPage"));
const StaffTVMenu = lazy(() => import("@/pages/StaffTVMenu"));
const StaffLocations = lazy(() => import("@/pages/StaffLocations"));
const StaffHeroCarousel = lazy(() => import("@/pages/StaffHeroCarousel"));
const StaffPromoCodes = lazy(() => import("@/pages/StaffPromoCodes"));
const TVDisplayPage = lazy(() => import("@/pages/TVDisplayPage"));

// Components - keep critical ones non-lazy
import PromoPopup from "@/components/PromoPopup";
import PromoOptInModal from "@/components/PromoOptInModal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-ha-bg flex items-center justify-center">
    <div className="text-ha-muted text-sm font-body tracking-widest uppercase animate-pulse">Loading</div>
  </div>
);

// Layout wrapper - hides navbar/footer/chat on standalone pages like /tv
function AppLayout({ children }) {
  const location = useLocation();
  const isStandalone = location.pathname === '/tv';
  
  if (isStandalone) {
    return children;
  }

  return (
    <>
      <Navbar />
      {children}
      <Footer />
      <PromoPopup />
    </>
  );
}

function App() {
  const [ageVerified, setAgeVerified] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showAgeGate, setShowAgeGate] = useState(false);

  useEffect(() => {
    // Check age verification
    const verified = localStorage.getItem('ageVerified');
    if (verified === 'true') {
      setAgeVerified(true);
    } else {
      const path = window.location.pathname;
      const isEmployeePage = path.startsWith('/staff') || path.startsWith('/login/employee');
      const isWholesalePage = path.startsWith('/wholesale') || path.startsWith('/login/wholesale') || path.startsWith('/signup/wholesale');
      const isTVDisplay = path === '/tv';
      
      if (!isEmployeePage && !isWholesalePage && !isTVDisplay) {
        setShowAgeGate(true);
      }
    }

    // Check auth token
    const token = localStorage.getItem('authToken');
    if (token) {
      checkAuth(token);
    } else {
      setLoading(false);
    }

    // Load cart
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        localStorage.removeItem('cart');
      }
    }
  }, []);

  const checkAuth = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('authToken');
    } finally {
      setLoading(false);
    }
  };

  const verifyAge = () => {
    localStorage.setItem('ageVerified', 'true');
    setAgeVerified(true);
    setShowAgeGate(false);
  };

  const login = (userData, token) => {
    localStorage.setItem('authToken', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  const addToCart = (product, quantity = 1) => {
    const newCart = [...cart];
    const cartItemId = product.selectedSize ? `${product.id}-${product.selectedSize}` : product.id;
    const existingIndex = newCart.findIndex(item => {
      const itemCartId = item.selectedSize ? `${item.id}-${item.selectedSize}` : item.id;
      return itemCartId === cartItemId;
    });
    
    if (existingIndex >= 0) {
      newCart[existingIndex].quantity += quantity;
    } else {
      newCart.push({ ...product, quantity, cartItemId });
    }
    
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));

    // Meta Pixel: AddToCart
    trackAddToCart({
      id: product.id,
      name: product.name,
      category: product.category,
      price: typeof product.price === 'number' ? product.price : undefined,
      quantity,
    });
  };

  const removeFromCart = (cartItemId) => {
    const newCart = cart.filter(item => {
      const itemId = item.cartItemId || (item.selectedSize ? `${item.id}-${item.selectedSize}` : item.id);
      return itemId !== cartItemId;
    });
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const updateCartQuantity = (cartItemId, quantity) => {
    const newCart = cart.map(item => {
      const itemId = item.cartItemId || (item.selectedSize ? `${item.id}-${item.selectedSize}` : item.id);
      return itemId === cartItemId ? { ...item, quantity } : item;
    });
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  if (loading) {
    return <PageLoader />;
  }

  const authValue = {
    user,
    setUser,
    login,
    logout,
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={authValue}>
        <div className="App min-h-screen bg-ha-bg text-ha-ink ha-grain">
          {showAgeGate && <AgeGate onVerify={verifyAge} />}
          <PromoOptInModal />
          
          <BrowserRouter>
            <ScrollToTop />
            <AppLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* TV Display - standalone kiosk mode */}
                  <Route path="/tv" element={<TVDisplayPage />} />

                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPageOptimized />} />
                <Route path="/products/:id" element={<ProductDetailPageOptimized />} />
                <Route path="/coas" element={<COAsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/retailers" element={<RetailersPage />} />
                <Route path="/locations" element={<LocationsPage />} />
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/sales-policy" element={<SalesPolicyPage />} />
                <Route path="/compliance" element={<CompliancePolicyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={!user ? <LoginChoice /> : <Navigate to={user.role === 'staff' ? "/staff/dashboard" : user.role === 'wholesale' ? "/wholesale/products" : "/dashboard"} />} />
                <Route path="/login/customer" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
                <Route path="/login/employee" element={!user ? <EmployeeLoginPage /> : <Navigate to="/staff/dashboard" />} />
                <Route path="/login/wholesale" element={!user ? <WholesaleLoginPage /> : <Navigate to="/wholesale/products" />} />
                <Route path="/signup" element={!user ? <CustomerSignupPage /> : <Navigate to="/dashboard" />} />
                <Route path="/signup/customer" element={!user ? <CustomerSignupPage /> : <Navigate to="/dashboard" />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/signup/wholesale" element={!user ? <WholesaleSignupPage /> : <Navigate to="/wholesale/products" />} />
                
                {/* Customer Routes */}
                <Route path="/dashboard" element={user && user.role === 'customer' ? <CustomerDashboard /> : <Navigate to="/login" />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={user && user.role === 'customer' ? <CheckoutPage /> : <Navigate to="/login" />} />
                
                {/* Wholesale Routes */}
                <Route path="/wholesale/products" element={user && user.role === 'wholesale' ? <WholesaleProductsPage /> : <Navigate to="/login/wholesale" />} />
                <Route path="/wholesale/checkout" element={user && user.role === 'wholesale' ? <WholesaleCheckoutPage /> : <Navigate to="/login/wholesale" />} />
                
                {/* Staff Routes */}
                <Route path="/staff/signup" element={!user ? <StaffSignupPage /> : <Navigate to="/staff/dashboard" />} />
                <Route path="/staff/login" element={!user ? <StaffLoginPage /> : <Navigate to="/staff/dashboard" />} />
                <Route path="/staff/dashboard" element={user && user.role === 'staff' ? <StaffDashboard /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/products" element={user && user.role === 'staff' ? <StaffProducts /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/wholesale-products" element={user && user.role === 'staff' ? <StaffWholesaleProducts /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/coas" element={user && user.role === 'staff' ? <StaffCOAs /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/orders" element={user && user.role === 'staff' ? <StaffOrders /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/all-orders" element={user && user.role === 'staff' ? <StaffAllOrders /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/content" element={user && user.role === 'staff' ? <StaffContent /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/home-banners" element={user && user.role === 'staff' ? <StaffHomeBanners /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/id-verifications" element={user && user.role === 'staff' ? <StaffIdVerifications /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/settings" element={user && user.role === 'staff' ? <StaffSettings /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/admin-settings" element={user && user.role === 'staff' ? <StaffAdminSettings /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/system" element={user && user.role === 'staff' ? <StaffSystemManagement /> : <Navigate to="/staff/login" />} />
                <Route path="/vita-verde" element={<VitaVerdePage />} />
                <Route path="/scavenger-hunt" element={<ScavengerHuntPage />} />
                <Route path="/staff/tv-menu" element={<StaffTVMenu />} />
                <Route path="/staff/promotions" element={user && user.role === 'staff' ? <StaffPromotions /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/promo-codes" element={user && user.role === 'staff' ? <StaffPromoCodes /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/sms-broadcast" element={user && user.role === 'staff' ? <StaffSMSBroadcast /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/files" element={user && user.role === 'staff' ? <StaffFiles /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/locations" element={user && user.role === 'staff' ? <StaffLocations /> : <Navigate to="/staff/login" />} />
                <Route path="/staff/hero-carousel" element={user && user.role === 'staff' ? <StaffHeroCarousel /> : <Navigate to="/staff/login" />} />
              </Routes>
            </Suspense>
            </AppLayout>
            <Toaster position="top-right" />
          </BrowserRouter>
        </div>
      </AuthContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
